import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { InvoiceData } from '../models/invoice-data.interface';
import { BasePdfTemplate } from './base-template';
import { getItemUnitName, getSelectedMappingLines } from './helpers/item-display.helper';

/**
 * PTR Enterprise Template - A4 Size
 * Standard professional invoice template
 */
export class PtrEnterpriseTemplate extends BasePdfTemplate {
  override templateName = 'ptr-enterprise';
  override templateVersion = '1.0.0';
  override description =
    'PTR Enterprise invoice template - A4 size standard layout';
  override pageSize: 'A4' = 'A4';
  override previewImage = 'assets/sampleoutput/ptr-enterprise-sample.png';

  private numberToWords(num: number): string {
    const ones = [
      '',
      'One',
      'Two',
      'Three',
      'Four',
      'Five',
      'Six',
      'Seven',
      'Eight',
      'Nine',
    ];
    const tens = [
      '',
      '',
      'Twenty',
      'Thirty',
      'Forty',
      'Fifty',
      'Sixty',
      'Seventy',
      'Eighty',
      'Ninety',
    ];
    const teens = [
      'Ten',
      'Eleven',
      'Twelve',
      'Thirteen',
      'Fourteen',
      'Fifteen',
      'Sixteen',
      'Seventeen',
      'Eighteen',
      'Nineteen',
    ];

    if (num === 0) return 'Zero';

    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100)
        return (
          tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '')
        );
      return (
        ones[Math.floor(n / 100)] +
        ' Hundred' +
        (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '')
      );
    };

    const wholePart = Math.floor(num);

    if (wholePart < 1000) return convertLessThanThousand(wholePart);
    if (wholePart < 100000) {
      const thousands = Math.floor(wholePart / 1000);
      const remainder = wholePart % 1000;
      return (
        convertLessThanThousand(thousands) +
        ' Thousand' +
        (remainder !== 0 ? ' ' + convertLessThanThousand(remainder) : '')
      );
    }

    const lakhs = Math.floor(wholePart / 100000);
    const remainder = wholePart % 100000;
    return (
      convertLessThanThousand(lakhs) +
      ' Lakh' +
      (remainder !== 0 ? ' ' + this.numberToWords(remainder) : '')
    );
  }

  override generatePdfDefinition(data: InvoiceData): TDocumentDefinitions {
    const storename = JSON.parse(localStorage.getItem('storename') || '{}');
    let storeAddress = '';
    if (storename) {
      const addressParts = [
        storename.address,
        storename.address1,
        storename.address2,
        storename.pincode,
      ].filter((part) => part && part.trim());

      storeAddress = addressParts.join(', ');
    }

    const businessName = storename?.name || data.businessName || '';
    const businessAddress = storeAddress || data.businessAddress || '';
    const businessPhone = storename?.mobile || data.businessPhone || '';
    const businessGSTIN = data.businessGSTIN || '';

    // Store reference to formatDate method for use in PDF definition
    const formatDate = this.formatDate.bind(this);
    const numberToWords = this.numberToWords.bind(this);
    let disAfterTax: any = 0;
    const taxableAmountValue = disAfterTax
      ? data.subtotal
      : data.subtotal - (data.discount || 0);
    console.log(data.items, 'itesm');

    // Free items summary (shown below the items table only when free items exist)
    let totalFreeQty = 0;
    let totalFreeAmount = 0;
    (data.items || []).forEach((item: any) => {
      if (Number(item.is_free) === 1) {
        totalFreeQty += Number(item.quantity || 0);
        totalFreeAmount += Number(item.originalprice || item.mrp || 0) * Number(item.quantity || 0);
      }
    });

    // Gross Amount excluding free items (backend subtotal includes free item amount)
    const freeItemsAmount = (data.items || []).reduce((sum: number, item: any) =>
      Number(item.is_free) === 1
        ? sum + Number(item.amount || (item.price || 0) * (item.quantity || 0))
        : sum, 0);
    const grossAmountExclFree = Math.max(0, Number(data.subtotal || 0) - freeItemsAmount);
    return {
      pageSize: this.getPageSize(),
      pageOrientation: 'portrait',
      pageMargins: [30, 30, 30, 30],

      content: [
        // Header Section with Logo, Business Name, and Tax Invoice
        {
          table: {
            widths: [80, '*', 120],
            body: [
              [
                // Logo placeholder (left)
                {
                  text: '',
                  margin: [5, 5, 5, 5],
                },
                // Business Name and Address (center)
                {
                  stack: [
                    {
                      text: businessName || 'PTR ENTERPRISES',
                      fontSize: 18,
                      bold: true,
                      alignment: 'center',
                    },
                    {
                      text: businessAddress,
                      fontSize: 8,
                      alignment: 'center',
                      margin: [0, 3, 0, 2],
                    },
                    {
                      text: businessPhone ? `Tel: ${businessPhone}` : '',
                      fontSize: 8,
                      alignment: 'center',
                      margin: [0, 1, 0, 1],
                    },
                    {
                      text: businessGSTIN ? `GSTIN: ${businessGSTIN}` : '',
                      fontSize: 8,
                      alignment: 'center',
                      bold: true,
                      margin: [0, 1, 0, 0],
                    },
                  ],
                  margin: [0, 5, 0, 5],
                },
                // Tax Invoice Label (right)
                {
                  stack: [
                    {
                      text:
                        data.invoiceType === 'quotation'
                          ? 'QUOTATION'
                          : 'TAX INVOICE',
                      fontSize: 10,
                      bold: true,
                      alignment: 'center',
                    },
                    {
                      text: 'ORIGINAL',
                      fontSize: 8,
                      alignment: 'center',
                      margin: [0, 2, 0, 0],
                    },
                  ],
                  margin: [5, 10, 5, 5],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
            hLineColor: function () {
              return '#000000';
            },
            vLineColor: function () {
              return '#000000';
            },
          },
          margin: [0, 0, 0, 0],
        },

        // Customer Details Section (To:) and Invoice Details
        {
          table: {
            widths: ['*', 180],
            body: [
              [
                // Left: Customer Details
                {
                  stack: [
                    {
                      text: 'To,',
                      fontSize: 8,
                      bold: true,
                      margin: [0, 0, 0, 2],
                    },
                    {
                      text: data.customerName || '',
                      fontSize: 8,
                      margin: [0, 0, 0, 1],
                    },
                    {
                      text: this.getCustomerAddressLines(data).join('\n'),
                      fontSize: 8,
                      margin: [0, 0, 0, 1],
                    },
                    {
                      text: data.customerMobile
                        ? `Tel: ${data.customerMobile}`
                        : '',
                      fontSize: 8,
                      margin: [0, 0, 0, 1],
                    },
                    {
                      text: '',
                      fontSize: 8,
                      margin: [0, 0, 0, 0],
                    },
                  ],
                  margin: [5, 5, 5, 5],
                },
                // Right: Invoice Details
                {
                  stack: [
                    {
                      columns: [
                        {
                          text: 'Invoice No:',
                          fontSize: 8,
                          bold: true,
                          width: 80,
                        },
                        { text: data.invoiceNumber, fontSize: 8, width: '*' },
                      ],
                      margin: [0, 0, 0, 2],
                    },
                    {
                      columns: [
                        {
                          text: 'Invoice Date:',
                          fontSize: 8,
                          bold: true,
                          width: 80,
                        },
                        {
                          text: formatDate(
                            (data.invoiceDate || data.invoicedate || '') as
                            | string
                            | Date,
                          ),
                          fontSize: 8,
                          width: '*',
                        },
                      ],
                      margin: [0, 0, 0, 2],
                    },
                    data.invoiceType === 'quotation' &&
                      data.quotationValidityDate
                      ? {
                        columns: [
                          {
                            text: 'Valid Until:',
                            fontSize: 8,
                            bold: true,
                            width: 80,
                          },
                          {
                            text: formatDate(
                              (data.quotationValidityDate || '') as
                              | string
                              | Date,
                            ),
                            fontSize: 8,
                            width: '*',
                          },
                        ],
                        margin: [0, 0, 0, 2],
                      }
                      : null,
                    {
                      columns: [
                        { text: '', fontSize: 8, bold: true, width: 80 },
                        { text: '', fontSize: 8, width: '*' },
                      ],
                      margin: [0, 0, 0, 0],
                    },
                  ].filter(Boolean),
                  margin: [5, 5, 5, 5],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
            hLineColor: function () {
              return '#000000';
            },
            vLineColor: function () {
              return '#000000';
            },
          },
          margin: [0, 0, 0, 0],
        },

        // Items Table
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 35, 25, 25, 45, 50, 35, 55],
            body: [
              // Header Row
              [
                { text: 'S.No.', fontSize: 8, bold: true, alignment: 'center' },
                { text: 'Item Description', fontSize: 8, bold: true },
                { text: 'HSN', fontSize: 8, bold: true, alignment: 'center' },
                { text: 'Qty', fontSize: 8, bold: true, alignment: 'center' },
                { text: 'Unit', fontSize: 8, bold: true, alignment: 'center' },
                { text: 'Price', fontSize: 8, bold: true, alignment: 'right' },
                {
                  text: 'Discount',
                  fontSize: 8,
                  bold: true,
                  alignment: 'right',
                },
                { text: 'Tax', fontSize: 8, bold: true, alignment: 'right' },
                { text: 'Amount', fontSize: 8, bold: true, alignment: 'right' },
              ],
              // Data Rows

              ...data.items.map((item, index) => {
                // item.total is taxable amount (after item discount)
                disAfterTax = item.discaftertax || 0;
                // const taxableAmount = item.total || 0;
                // const taxAmount = item.taxamount || 0;
                // // Amount = taxable + tax
                // const itemAmount = taxableAmount + taxAmount;
                const taxableAmount = item.discaftertax
                  ? (item.price * item.quantity)
                  : (item.total || 0);
                const taxAmount = item.taxamount || 0;
                const itemAmount = taxableAmount + taxAmount;


                return [
                  {
                    text: (index + 1).toString(),
                    fontSize: 8,
                    alignment: 'center',
                  },
                  // { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, fontSize: 8 },
                  getSelectedMappingLines(item).length > 0
                    ? {
                      stack: [
                        { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, fontSize: 8 },
                        ...getSelectedMappingLines(item).map((line: string) => ({ text: line, fontSize: 7 })),
                      ],
                    }
                    : { text: `${item.name || ''}${Number(item.is_free) === 1 ? ' [FREE]' : ''}`, fontSize: 8 },
                  { text: item.hsn || '', fontSize: 8, alignment: 'center' },
                  {
                    text: item.quantity.toString(),
                    fontSize: 8,
                    alignment: 'center',
                  },
                  // { text: item.unit?.name, fontSize: 8, alignment: 'center' },
                  { text: getItemUnitName(item), fontSize: 8, alignment: 'center' },
                  {
                    text: this.formatCurrency(item.price),
                    fontSize: 8,
                    alignment: 'right',
                  },
                  {
                    // text: item.discount
                    //   ? `${this.formatCurrency(item.discount)} (${item.discountPercentage || 0}%)`
                    //   : '0.00 (0%)',
                    // Free items print zero discount
                    text: Number(item.is_free) !== 1 && item.discount
                      ? `${this.formatCurrency(item.discount)} (${item.discountPercentage || 0}%)`
                      : '0.00 (0%)',
                    fontSize: 8,
                    alignment: 'right',
                  },
                  {
                    text: Number(item.is_free) !== 1 && item.tax ? `${item.tax}%` : '0%',
                    fontSize: 8,
                    alignment: 'right',
                  },
                  {
                    text: this.formatCurrency(Number(item.is_free) === 1 ? 0 : itemAmount),
                    fontSize: 8,
                    alignment: 'right',
                    bold: true,
                  },
                ];
              }),
            ],
          },
          layout: {
            hLineWidth: function (i: number, node: any) {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
            hLineColor: function () {
              return '#000000';
            },
            vLineColor: function () {
              return '#000000';
            },
            paddingLeft: function () {
              return 3;
            },
            paddingRight: function () {
              return 3;
            },
            paddingTop: function () {
              return 3;
            },
            paddingBottom: function () {
              return 3;
            },
          },
          margin: [0, 0, 0, 0],
        },

        // Free Items line (only when free items exist)
        ...(totalFreeQty > 0
          ? [
            {
              text: `Free Items : ${totalFreeQty}   ${this.formatCurrency(totalFreeAmount)}`,
              fontSize: 8,
              bold: true,
              alignment: 'right',
              margin: [0, 2, 3, 2],
            } as any,
          ]
          : []),

        // Tax Summary and Gross Amount Section (50-50 split)
        {
          table: {
            widths: ['*', '*'],
            body: [
              [
                // Left side: Tax table (IGST or CGST/SGST)
                {
                  table: this.isInterStateTax(data)
                    ? // Inter-state: Show IGST only
                    {
                      widths: ['*', 40, 50],
                      body: [
                        [
                          {
                            text: '',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: 'IGST',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                            colSpan: 2,
                          },
                          {},
                        ],
                        [
                          {
                            text: 'Taxable Amt',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: '%',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: 'Amount',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                        ],
                        [
                          {

                            text: this.formatCurrency(
                              // data.subtotal - (data.discount || 0),
                              // disAfterTax ? data.subtotal : data.subtotal - (data.discount || 0)
                              disAfterTax ? grossAmountExclFree : grossAmountExclFree - (data.discount || 0)

                            ),
                            fontSize: 8,
                            alignment: 'center',
                          },
                          {
                            text: data.igst
                              // ? `${((data.igst / (data.subtotal - (data.discount || 0))) * 100).toFixed(0)}%`
                              ? `${((data.igst / (grossAmountExclFree - (data.discount || 0))) * 100).toFixed(0)}%`
                              : '0%',
                            fontSize: 8,
                            alignment: 'center',
                          },
                          {
                            text: this.formatCurrency(data.igst || 0),
                            fontSize: 8,
                            alignment: 'center',
                          },
                        ],
                      ],
                    }
                    : // Intra-state: Show CGST + SGST
                    {
                      widths: ['*', 40, 50, 40, 50],
                      body: [
                        [
                          {
                            text: '',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: 'CGST',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                            colSpan: 2,
                          },
                          {},
                          {
                            text: 'SGST',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                            colSpan: 2,
                          },
                          {},
                        ],
                        [
                          {
                            text: 'Taxable Amt',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: '%',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: 'Amount',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: '%',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                          {
                            text: 'Amount',
                            fontSize: 8,
                            bold: true,
                            alignment: 'center',
                          },
                        ],
                        [
                          {
                            text: this.formatCurrency(
                              // data.subtotal - (data.discount || 0),
                              // disAfterTax ? data.subtotal : data.subtotal - (data.discount || 0)
                              disAfterTax ? grossAmountExclFree : grossAmountExclFree - (data.discount || 0)

                            ),
                            fontSize: 8,
                            alignment: 'center',
                          },
                          {
                            text: data.cgst
                              // ? `${((data.cgst / (data.subtotal - (data.discount || 0))) * 100).toFixed(0)}%`
                              ? `${((data.cgst / (grossAmountExclFree - (data.discount || 0))) * 100).toFixed(0)}%`
                              : '0%',
                            fontSize: 8,
                            alignment: 'center',
                          },
                          {
                            text: this.formatCurrency(data.cgst || 0),
                            fontSize: 8,
                            alignment: 'center',
                          },
                          {
                            text: data.sgst
                              // ? `${((data.sgst / (data.subtotal - (data.discount || 0))) * 100).toFixed(0)}%`
                              ? `${((data.sgst / (grossAmountExclFree - (data.discount || 0))) * 100).toFixed(0)}%`
                              : '0%',
                            fontSize: 8,
                            alignment: 'center',
                          },
                          {
                            text: this.formatCurrency(data.sgst || 0),
                            fontSize: 8,
                            alignment: 'center',
                          },
                        ],
                      ],
                    },
                  layout: {
                    hLineWidth: function () {
                      return 1;
                    },
                    vLineWidth: function () {
                      return 1;
                    },
                    hLineColor: function () {
                      return '#000000';
                    },
                    vLineColor: function () {
                      return '#000000';
                    },
                  },
                  margin: [0, 0, 0, 0],
                },
                // Right side: Gross Amount breakdown
                {
                  table: {
                    widths: ['*', 80],
                    body: [
                      [
                        {
                          text: 'Gross Amount',
                          fontSize: 8,
                          bold: true,
                          alignment: 'right',
                        },
                        {
                          // text: this.formatCurrency(data.subtotal),
                          text: this.formatCurrency(grossAmountExclFree),
                          fontSize: 8,
                          alignment: 'right',
                        },
                      ],
                      ...(this.isInterStateTax(data)
                        ? [
                          // Inter-state: Show IGST only
                          [
                            { text: 'IGST', fontSize: 8, alignment: 'right' },
                            {
                              text: this.formatCurrency(data.igst || 0),
                              fontSize: 8,
                              alignment: 'right',
                            },
                          ],
                        ]
                        : [
                          // Intra-state: Show CGST + SGST
                          [
                            { text: 'SGST', fontSize: 8, alignment: 'right' },
                            {
                              text: this.formatCurrency(data.sgst || 0),
                              fontSize: 8,
                              alignment: 'right',
                            },
                          ],
                          [
                            { text: 'CGST', fontSize: 8, alignment: 'right' },
                            {
                              text: this.formatCurrency(data.cgst || 0),
                              fontSize: 8,
                              alignment: 'right',
                            },
                          ],
                        ]),
                      [
                        {
                          text: 'Rounded Off',
                          fontSize: 8,
                          alignment: 'right',
                        },
                        {
                          text: this.formatCurrency(
                            Math.round(
                              data.totalAmount || data.grandTotal || 0,
                            ) - (data.totalAmount || data.grandTotal || 0),
                          ),
                          fontSize: 8,
                          alignment: 'right',
                        },
                      ],
                    ],
                  },
                  layout: 'noBorders',
                  margin: [0, 0, 0, 0],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
            hLineColor: function () {
              return '#000000';
            },
            vLineColor: function () {
              return '#000000';
            },
            paddingLeft: function () {
              return 0;
            },
            paddingRight: function () {
              return 0;
            },
            paddingTop: function () {
              return 0;
            },
            paddingBottom: function () {
              return 0;
            },
          },
          margin: [0, 0, 0, 0],
        },

        // TOTAL Row (Full Width) - Grand Total before overall discount
        {
          table: {
            widths: ['*', 80],
            body: [
              [
                { text: 'TOTAL', fontSize: 9, bold: true, alignment: 'right' },
                {
                  text: this.formatCurrency(
                    Math.round(data.totalAmount || data.grandTotal || 0),
                  ),
                  fontSize: 9,
                  bold: true,
                  alignment: 'right',
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
            hLineColor: function () {
              return '#000000';
            },
            vLineColor: function () {
              return '#000000';
            },
            paddingLeft: function () {
              return 3;
            },
            paddingRight: function () {
              return 3;
            },
            paddingTop: function () {
              return 3;
            },
            paddingBottom: function () {
              return 3;
            },
          },
          margin: [0, 0, 0, 0],
        },

        // Overall Discount Row (only if overall discount exists)
        ...(data.overallDiscount && data.overallDiscount > 0
          ? [
            {
              table: {
                widths: ['*', 80],
                body: [
                  [
                    {
                      text: 'Overall Discount',
                      fontSize: 9,
                      bold: true,
                      alignment: 'right',
                    },
                    {
                      text: `- ${this.formatCurrency(data.overallDiscount)}`,
                      fontSize: 9,
                      bold: true,
                      alignment: 'right',
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: function () {
                  return 1;
                },
                vLineWidth: function () {
                  return 1;
                },
                hLineColor: function () {
                  return '#000000';
                },
                vLineColor: function () {
                  return '#000000';
                },
                paddingLeft: function () {
                  return 3;
                },
                paddingRight: function () {
                  return 3;
                },
                paddingTop: function () {
                  return 3;
                },
                paddingBottom: function () {
                  return 3;
                },
              },
              margin: [0, 0, 0, 0] as [number, number, number, number],
            },
          ]
          : []),

        ...((Number(data.loyaltyAmount) || 0) > 0
          ? [
            {
              table: {
                widths: ['*', 80],
                body: [
                  [
                    {
                      text: `Loyalty Discount (${Number(data.loyaltyPoints) || 0} pts)`,
                      fontSize: 9,
                      bold: true,
                      alignment: 'right',
                    },
                    {
                      text: `- ${this.formatCurrency(Number(data.loyaltyAmount) || 0)}`,
                      fontSize: 9,
                      bold: true,
                      alignment: 'right',
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: function () {
                  return 1;
                },
                vLineWidth: function () {
                  return 1;
                },
                hLineColor: function () {
                  return '#000000';
                },
                vLineColor: function () {
                  return '#000000';
                },
                paddingLeft: function () {
                  return 3;
                },
                paddingRight: function () {
                  return 3;
                },
                paddingTop: function () {
                  return 3;
                },
                paddingBottom: function () {
                  return 3;
                },
              },
              margin: [0, 0, 0, 0] as [number, number, number, number],
            },
          ]
          : []),

        ...((data.overallDiscount && data.overallDiscount > 0) ||
          (Number(data.loyaltyAmount) || 0) > 0
          ? [
            {
              table: {
                widths: ['*', 80],
                body: [
                  [
                    {
                      text: 'NET AMOUNT',
                      fontSize: 10,
                      bold: true,
                      alignment: 'right',
                    },
                    {
                      text: this.formatCurrency(
                        Math.round(
                          (data.totalAmount || data.grandTotal || 0) -
                          (data.overallDiscount || 0) -
                          (Number(data.loyaltyAmount) || 0),
                        ),
                      ),
                      fontSize: 10,
                      bold: true,
                      alignment: 'right',
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: function () {
                  return 1;
                },
                vLineWidth: function () {
                  return 1;
                },
                hLineColor: function () {
                  return '#000000';
                },
                vLineColor: function () {
                  return '#000000';
                },
                paddingLeft: function () {
                  return 3;
                },
                paddingRight: function () {
                  return 3;
                },
                paddingTop: function () {
                  return 3;
                },
                paddingBottom: function () {
                  return 3;
                },
              },
              margin: [0, 0, 0, 0] as [number, number, number, number],
            },
          ]
          : []),

        // Bank Details and Amount Chargeable Section (50-50 split)
        {
          table: {
            widths: ['*', '*'],
            body: [
              [
                // Left: Bank Details (50%)
                {
                  stack: [
                    {
                      text: "Company's Bank Details :",
                      fontSize: 8,
                      bold: true,
                      margin: [0, 0, 0, 3],
                    },
                    {
                      text: 'Bank Name :',
                      fontSize: 7,
                      margin: [0, 0, 0, 1],
                    },
                    {
                      text: 'Branch Name :',
                      fontSize: 7,
                      margin: [0, 0, 0, 1],
                    },
                    {
                      text: 'Account No :',
                      fontSize: 7,
                      margin: [0, 0, 0, 1],
                    },
                    {
                      text: 'IFSC Code :',
                      fontSize: 7,
                      margin: [0, 0, 0, 0],
                    },
                  ],
                  margin: [5, 5, 5, 5],
                },
                // Right: Amount Chargeable (50%)
                {
                  stack: [
                    {
                      text: 'Amount Chargeable (in words)',
                      fontSize: 7,
                      bold: true,
                      margin: [0, 0, 0, 2],
                    },
                    {
                      text:
                        numberToWords(
                          Math.round(
                            (data.totalAmount || data.grandTotal || 0) -
                            (data.overallDiscount || 0) -
                            (Number(data.loyaltyAmount) || 0),
                          ),
                        ).toUpperCase() + ' ONLY',
                      fontSize: 8,
                      bold: true,
                      margin: [0, 0, 0, 0],
                    },
                  ],
                  margin: [5, 5, 5, 5],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
            hLineColor: function () {
              return '#000000';
            },
            vLineColor: function () {
              return '#000000';
            },
            paddingLeft: function () {
              return 3;
            },
            paddingRight: function () {
              return 3;
            },
            paddingTop: function () {
              return 3;
            },
            paddingBottom: function () {
              return 3;
            },
          },
          margin: [0, 0, 0, 0],
        },

        // Declaration and Signature (50-50 split)
        {
          table: {
            widths: ['*', '*'],
            body: [
              [
                // Left: Declaration (50%)
                {
                  stack: [
                    {
                      text: 'Declaration:',
                      fontSize: 8,
                      bold: true,
                      margin: [0, 0, 0, 3],
                    },
                    {
                      text: '* Goods once sold will not be taken back or exchanged.',
                      fontSize: 7,
                      bold: true,
                      margin: [0, 0, 0, 0],
                    },
                  ],
                  margin: [5, 5, 5, 5],
                },
                // Right: Signature (50%)
                {
                  stack: [
                    {
                      text: 'For ' + (businessName || 'PTR ENTERPRISES'),
                      fontSize: 8,
                      bold: true,
                      alignment: 'right',
                      margin: [0, 0, 0, 30],
                    },
                    {
                      text: 'Verified By',
                      fontSize: 7,
                      alignment: 'right',
                      margin: [0, 0, 0, 1],
                    },
                    {
                      text: 'Authorized Signatory',
                      fontSize: 7,
                      alignment: 'right',
                      margin: [0, 0, 0, 0],
                    },
                  ],
                  margin: [5, 5, 5, 5],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: function () {
              return 1;
            },
            vLineWidth: function () {
              return 1;
            },
            hLineColor: function () {
              return '#000000';
            },
            vLineColor: function () {
              return '#000000';
            },
            paddingLeft: function () {
              return 3;
            },
            paddingRight: function () {
              return 3;
            },
            paddingTop: function () {
              return 3;
            },
            paddingBottom: function () {
              return 3;
            },
          },
          margin: [0, 0, 0, 0],
        },

        // Footer
        {
          text: 'This is Computer Generated Invoice.',
          fontSize: 7,
          alignment: 'center',
          margin: [0, 5, 0, 0],
        },
      ],

      styles: {
        tableHeader: {
          fontSize: 10,
          bold: true,
          color: '#1565c0',
        },
        tableCell: {
          fontSize: 9,
        },
      },
    };
  }

  private getTotalCGST(invoice: InvoiceData): number {
    // return (
    //   invoice.cgst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.cgstval || product.cgstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.cgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.cgstval || product.cgstAmount || 0),
        0,
      )
    );
  }

  private getTotalSGST(invoice: InvoiceData): number {
    // return (
    //   invoice.sgst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.sgstval || product.sgstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.sgst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.sgstval || product.sgstAmount || 0),
        0,
      )
    );
  }

  private getTotalIGST(invoice: InvoiceData): number {
    // return (
    //   invoice.igst ||
    //   invoice.items.reduce(
    //     (sum: any, product: any) =>
    //       sum + (product.igstval || product.igstAmount || 0),
    //     0,
    //   )
    // );
    // Fallback item sum excludes free items
    return (
      invoice.igst ||
      invoice.items.reduce(
        (sum: any, product: any) =>
          Number(product.is_free) === 1
            ? sum
            : sum + (product.igstval || product.igstAmount || 0),
        0,
      )
    );
  }

  private isInterStateTax(invoice: InvoiceData): boolean {
    const igst = this.getTotalIGST(invoice);
    const cgst = this.getTotalCGST(invoice);
    const sgst = this.getTotalSGST(invoice);

    return igst > 0 && cgst === 0 && sgst === 0;
  }
}
